import { Col, Form, message, Modal, Radio, Row, Select, Tabs } from "antd";
import { Rule } from "antd/lib/form";
import { productCategoryApi } from "api/product-category.api";
import { productApi } from "api/product.api";
import { AppButton, AppInput, AppTextArea } from "components/antd-ui";
import { MultiImageUpload } from "components/Upload/MultiImageUpload";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import { useEffect, useMemo, useState } from "react";
import { ModalStatus } from "types/modal";
import { Product } from "types/product";
import { ProductCategory } from "types/product-category";
import { InputNumber } from "components/Input/InputNumber";
import { ProductPropertyTab } from "./ProductPropertyTab";
import { getImageSize } from "utils";
import type { UploadFile } from "antd/lib/upload/interface";
import { AppSelect } from "components/antd-ui";

const rules: Rule[] = [{ required: true }];

interface ProductForm extends Product {
  productCategoryId: number;
}

export const ProductModal = ({
  visible,
  status,
  product,
  onClose,
  onSubmitOk,
}: {
  visible: boolean;
  status: ModalStatus;
  product: Partial<Product>;
  onClose: () => void;
  onSubmitOk: () => void;
}) => {
  const [form] = Form.useForm<ProductForm>();
  const [loading, setLoading] = useState(false);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    [],
  );
  const [images, setImages] = useState<string[]>([]);
  const [imageSize, setImageSize] = useState<any>({});

  useEffect(() => {
    if (status === "create" && visible) {
      form.resetFields();
    }

    if (visible) {
      fetchProductCategory();
    }
  }, [visible, status]);

  const fetchProductCategory = async () => {
    const res = await productCategoryApi.findAll({
      page: 1,
      limit: 0,
    });
    setProductCategories(res.data.productCategories);
  };

  useEffect(() => {
    form.setFieldsValue({
      ...product,
      productCategoryId: product.productCategory?.id,
    });
    getImageSize(setImageSize, form.getFieldValue("image"));
  }, [product]);

  const createData = async () => {
    await form.validateFields();
    const data = {
      product: form.getFieldsValue(),
      productCategoryId: form.getFieldValue("productCategoryId"),
      images,
    };

    setLoading(true);
    try {
      await productApi.create(data);
      message.success("Tạo sản phẩm thành công");
      onClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const updateData = async () => {
    await form.validateFields();
    const data = {
      product: form.getFieldsValue(),
      productCategoryId: form.getFieldValue("productCategoryId"),
      images,
    };

    setLoading(true);
    try {
      await productApi.update(product?.id || 0, data);
      message.success("Cập nhật sản phẩm thành công");
      onClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (status === "create") {
      await createData();
      return;
    }
    await updateData();
  };

  const imageList = useMemo<UploadFile<any>[]>(() => {
    if (product.images) {
      return product.images.map((e) => ({
        name: e.id + "",
        uid: e.id + "",
        url: e.url,
      }));
    }
    setImages([]);
    return [];
  }, [product.images]);

  return (
    <Modal
      style={{ top: 15 }}
      onCancel={onClose}
      open={visible}
      title={
        <h1 className="text-xl font-bold text-center">
          {status === "create" ? "Tạo mới sản phẩm" : "Cập nhật sản phẩm"}
        </h1>
      }
      footer={null}
      width={1200}
      className="app-form-modal"
    >
      <div className="app-form-modal__body">
        <section className="app-form-modal__section app-form-modal__tabs">
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: "1",
                label: "Thông tin",
                children: (
                  <Form layout="vertical" form={form}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Row gutter={16}>
                          <Col span={24}>
                            <Form.Item
                              label="Tên sản phẩm"
                              name="name"
                              rules={rules}
                            >
                              <AppInput placeholder="Nhập tên sản phẩm" />
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              label="Giá trước khuyến mãi"
                              name="originPrice"
                              rules={rules}
                            >
                              <InputNumber />
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              label="Giá sau khuyến mãi"
                              name="finalPrice"
                              rules={rules}
                            >
                              <InputNumber />
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              label="Tình trạng kho"
                              name="isOutOfStock"
                            >
                              <Radio.Group>
                                <Radio value={true}>Hết hàng</Radio>
                                <Radio value={false}>Còn hàng</Radio>
                              </Radio.Group>
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              label="Danh mục"
                              name="productCategoryId"
                              rules={rules}
                            >
                              <AppSelect
                                style={{ width: "100%" }}
                                options={productCategories.map((e) => ({
                                  value: e.id,
                                  label: e.name,
                                }))}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={24}>
                            <Form.Item
                              label="Mô tả"
                              name="description"
                              rules={rules}
                            >
                              <AppTextArea placeholder="Nhập mô tả" rows={5} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>

                      <Col span={12}>
                        <Form.Item shouldUpdate={true}>
                          {() => (
                            <Form.Item
                              label="Hình ảnh (Đề xuất kích thước tối đa 800 x 800)"
                              name="image"
                            >
                              <SingleImageUpload
                                onUploadOk={(path: string) => {
                                  form.setFieldsValue({ image: path });
                                  getImageSize(setImageSize, path);
                                }}
                                imageUrl={form.getFieldValue("image")}
                                uploadUrl={
                                  import.meta.env.VITE_API_URL +
                                  "/v1/shop/product/upload"
                                }
                              />
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                Kích thước hiện tại {imageSize.width} x{" "}
                                {imageSize.height}
                              </div>
                            </Form.Item>
                          )}
                        </Form.Item>

                        <Form.Item shouldUpdate={true}>
                          {() => (
                            <Form.Item
                              label="Ảnh khác (Đề xuất kích thước tối đa 800 x 800)"
                              name="thumbnails"
                            >
                              <MultiImageUpload
                                onUploadOk={(fileList) => setImages(fileList)}
                                fileListProp={imageList}
                              />
                            </Form.Item>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                ),
              },
              ...(status === "update"
                ? [
                    {
                      key: "2",
                      label: "Thuộc tính",
                      children: <ProductPropertyTab productId={product.id} />,
                    },
                  ]
                : []),
            ]}
          />
        </section>
      </div>
      <div className="ant-modal-footer">
        <div className="flex gap-2 justify-end">
          <AppButton onClick={onClose}>Hủy</AppButton>
          <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
            {status === "create" ? "Xác nhận" : "Cập nhật"}
          </AppButton>
        </div>
      </div>
    </Modal>
  );
};
